import React from 'react';
import styled from 'styled-components';

const Section = styled.section`
  padding: clamp(3rem, 6vw, 5rem) clamp(1.5rem, 4vw, 3rem);
  background: ${({ theme }) => theme.colors.offWhite ?? '#f8f7f3'};
  border-top: 1px solid rgba(16, 29, 64, 0.07);
  overflow: hidden;
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: clamp(2.5rem, 6vw, 5rem);
  flex-direction: ${({ $imageRight }) => ($imageRight ? 'row-reverse' : 'row')};

  @media (max-width: 820px) {
    flex-direction: column;
    gap: 2rem;
  }
`;

/* ── Image side ─────────────────────────────────────── */
const ImageWrap = styled.div`
  flex: 0 0 52%;
  position: relative;
  border-radius: 4px;
  overflow: hidden;

  /* drop-shadow sits outside clip-path, so we use filter on the wrapper */
  filter: drop-shadow(0 18px 40px rgba(16, 29, 64, 0.16))
          drop-shadow(0 4px 10px rgba(16, 29, 64, 0.09));

  @media (max-width: 820px) {
    flex: none;
    width: 100%;
  }
`;

const Img = styled.img`
  display: block;
  width: 100%;
  height: clamp(260px, 34vw, 420px);
  object-fit: cover;

  clip-path: ${({ $imageRight }) =>
    $imageRight
      ? 'polygon(14% 0%, 100% 0%, 100% 100%, 0% 100%)'
      : 'polygon(0% 0%, 100% 0%, 86% 100%, 0% 100%)'};

  @media (max-width: 820px) {
    height: 240px;
    clip-path: none;
    border-radius: 14px;
  }
`;

/* ── Text side ──────────────────────────────────────── */
const TextSide = styled.div`
  flex: 1;
  min-width: 0;
`;

const EyebrowRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 1rem;
`;

const EyebrowLine = styled.span`
  display: block;
  width: 2rem;
  height: 1.5px;
  background: ${({ theme }) => theme.colors.accent ?? '#9d6d49'};
  border-radius: 2px;
  opacity: 0.75;
`;

const Eyebrow = styled.span`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.accent ?? '#9d6d49'};
  opacity: 0.85;
`;

const Title = styled.h2`
  margin: 0 0 1rem;
  font-family: ${({ theme }) => theme.fonts.main};
  font-size: clamp(1.7rem, 3vw, 2.5rem);
  font-weight: 700;
  line-height: 1.15;
  color: ${({ theme }) => theme.colors.primary ?? '#101d40'};
  max-width: 20ch;
`;

const Description = styled.p`
  margin: 0 0 2rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.9rem, 1.55vw, 1.05rem);
  line-height: 1.7;
  color: rgba(16, 29, 64, 0.62);
  max-width: 42ch;
`;

const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${({ theme }) => theme.colors.accent ?? '#9d6d49'};
  color: #fff;
  border: none;
  padding: 0.75rem 1.75rem;
  border-radius: 999px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(157, 109, 73, 0.32);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &::after {
    content: '→';
    transition: transform 0.2s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 22px rgba(157, 109, 73, 0.4);
  }

  &:hover::after {
    transform: translateX(3px);
  }
`;

const ImageSlant = ({
  image,
  title,
  description,
  buttonText,
  buttonAction,
  tag,
  imagePosition = 'left',
  darkMode = false,
}) => {
  const isRight = imagePosition === 'right';

  return (
    <Section>
      <Inner $imageRight={isRight}>
        <ImageWrap>
          <Img src={image} alt={title} $imageRight={isRight} />
        </ImageWrap>

        <TextSide>
          <EyebrowRow>
            <EyebrowLine />
            {tag && <Eyebrow>{tag}</Eyebrow>}
          </EyebrowRow>
          <Title>{title}</Title>
          <Description>{description}</Description>
          {buttonText && (
            <Btn type="button" onClick={buttonAction}>
              {buttonText}
            </Btn>
          )}
        </TextSide>
      </Inner>
    </Section>
  );
};

export default ImageSlant;
